/**
 * WhatIfForm — Form to submit run_engine_whatif params.
 *
 * Renders diff_vs_current result using:
 * - FinancialValue for all money
 * - MetricCard for metrics
 * - StatusBadge for statuses
 *
 * NO raw number rendering.
 */

import React, { useState } from 'react';
import { Play, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FinancialValue } from '../shared/FinancialValue';
import { MetricCard } from '../shared/MetricCard';
import { StatusBadge } from '../shared/StatusBadge';
import { WhatIfRequest, WhatIfResult, DiffVsCurrent } from '../../hooks/useCopilot';

interface WhatIfFormProps {
  reportId: string;
  onRun: (request: WhatIfRequest) => Promise<WhatIfResult | null>;
  result: WhatIfResult | null;
  isLoading: boolean;
}

const COMMON_ENGINES = [
  { id: 'fair_settlement', label: 'Fair Settlement' },
  { id: 'bottom_up', label: 'Bottom-Up' },
  { id: 'dual_method', label: 'Dual Method' },
  { id: 'enhanced_claim', label: 'Enhanced Claim' },
  { id: 'epi', label: 'EPI (Extended Period)' },
];

const COMMON_OVERRIDES = [
  { field: 'restoration_days', label: 'Restoration Days', type: 'number', placeholder: 'e.g., 45' },
  { field: 'revenue_drop_pct', label: 'Revenue Drop %', type: 'number', placeholder: 'e.g., 0.25 for 25%' },
  { field: 'expense_reduction_pct', label: 'Expense Reduction %', type: 'number', placeholder: 'e.g., 0.15 for 15%' },
  { field: 'indemnity_days', label: 'Indemnity Days', type: 'number', placeholder: 'e.g., 60' },
];

/**
 * Extract numeric value from diff_vs_current object
 */
function extractMoneyValue(diff: DiffVsCurrent | null, path: string): number | null {
  if (!diff) return null;
  const value = diff[path];
  if (value && typeof value === 'object' && 'after' in value) {
    return (value as { after: number }).after;
  }
  if (typeof value === 'number') return value;
  return null;
}

/**
 * Extract delta value for display
 */
function extractDelta(diff: DiffVsCurrent | null, path: string): { delta: number; pctChange: number } | null {
  if (!diff) return null;
  const value = diff[path];
  if (value && typeof value === 'object') {
    const v = value as { delta?: number; pct_change?: number };
    if (typeof v.delta === 'number' && typeof v.pct_change === 'number') {
      return { delta: v.delta, pctChange: v.pct_change };
    }
  }
  return null;
}

/**
 * Recursively flatten diff object for display
 */
function flattenDiff(obj: DiffVsCurrent | null, prefix = ''): Array<{ key: string; label: string; value: unknown }> {
  if (!obj) return [];

  const result: Array<{ key: string; label: string; value: unknown }> = [];

  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    if (value && typeof value === 'object' && ('delta' in value || 'after' in value)) {
      result.push({ key: fullKey, label, value });
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result.push(...flattenDiff(value as DiffVsCurrent, fullKey));
    } else {
      result.push({ key: fullKey, label, value });
    }
  }

  return result;
}

export function WhatIfForm({ reportId, onRun, result, isLoading }: WhatIfFormProps) {
  const [engineId, setEngineId] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customField, setCustomField] = useState('');
  const [customValue, setCustomValue] = useState('');

  const handleRun = async () => {
    if (!engineId) return;

    // Convert string values to numbers where appropriate
    const parsedOverrides: Record<string, number | string> = {};
    for (const [key, value] of Object.entries(overrides)) {
      if (value.trim()) {
        const numValue = parseFloat(value);
        parsedOverrides[key] = isNaN(numValue) ? value : numValue;
      }
    }

    await onRun({
      reportId,
      engineId,
      overrides: parsedOverrides,
    });
  };

  const handleOverrideChange = (field: string, value: string) => {
    setOverrides((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCustomOverride = () => {
    if (customField && customValue) {
      const numValue = parseFloat(customValue);
      handleOverrideChange(customField, isNaN(numValue) ? customValue : String(numValue));
      setCustomField('');
      setCustomValue('');
    }
  };

  const handleReset = () => {
    setEngineId('');
    setOverrides({});
  };

  const hasResult = result && result.engine_id;
  const diffItems = hasResult ? flattenDiff(result.diff_vs_current) : [];

  return (
    <div className="space-y-4" data-testid="whatif-form">
      {/* Engine Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-label)]">
          Engine
        </label>
        <select
          value={engineId}
          onChange={(e) => setEngineId(e.target.value)}
          className={cn(
            'w-full px-3 py-2 rounded-md text-sm',
            'bg-[var(--color-bg-elevated)] border border-[var(--color-border)]',
            'text-[var(--color-text-primary)]',
            'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]',
            'transition-colors'
          )}
          data-testid="whatif-engine-select"
        >
          <option value="">Select an engine...</option>
          {COMMON_ENGINES.map((engine) => (
            <option key={engine.id} value={engine.id}>
              {engine.label}
            </option>
          ))}
        </select>
      </div>

      {/* Override Inputs */}
      <div className="space-y-3">
        <label className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-label)]">
          Scenario Overrides
        </label>

        {COMMON_OVERRIDES.map((override) => (
          <div key={override.field} className="space-y-1">
            <label className="text-xs text-[var(--color-text-muted)]">
              {override.label}
            </label>
            <input
              type={override.type}
              value={overrides[override.field] || ''}
              onChange={(e) => handleOverrideChange(override.field, e.target.value)}
              placeholder={override.placeholder}
              className={cn(
                'w-full px-3 py-2 rounded-md text-sm',
                'bg-[var(--color-bg-elevated)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]',
                'transition-colors'
              )}
              data-testid={`whatif-override-${override.field}`}
            />
          </div>
        ))}
      </div>

      {/* Advanced Overrides */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
      >
        {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        Advanced Overrides
      </button>

      {showAdvanced && (
        <div className="space-y-2 p-3 rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className="flex gap-2">
            <input
              type="text"
              value={customField}
              onChange={(e) => setCustomField(e.target.value)}
              placeholder="Field name"
              className={cn(
                'flex-1 px-3 py-2 rounded-md text-sm',
                'bg-[var(--color-bg-base)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]'
              )}
            />
            <input
              type="text"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder="Value"
              className={cn(
                'flex-1 px-3 py-2 rounded-md text-sm',
                'bg-[var(--color-bg-base)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]'
              )}
            />
            <button
              onClick={handleAddCustomOverride}
              disabled={!customField || !customValue}
              className={cn(
                'px-3 py-2 rounded-md text-sm font-medium',
                'bg-[var(--color-bg-base)] border border-[var(--color-brand)]',
                'text-[var(--color-brand)]',
                'hover:bg-[var(--color-brand)] hover:text-[var(--color-bg-base)]',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors'
              )}
            >
              Add
            </button>
          </div>

          {/* Show active custom overrides */}
          {Object.entries(overrides)
            .filter(([key]) => !COMMON_OVERRIDES.some((o) => o.field === key))
            .map(([key, value]) => (
              <div key={key} className="flex items-center justify-between text-sm">
                <span className="text-[var(--color-text-secondary)]">
                  {key}: <strong className="text-[var(--color-text-primary)]">{value}</strong>
                </span>
                <button
                  onClick={() => {
                    const { [key]: _, ...rest } = overrides;
                    setOverrides(rest);
                  }}
                  className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-brand)]"
                >
                  Remove
                </button>
              </div>
            ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={handleRun}
          disabled={!engineId || isLoading}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium',
            'bg-[var(--color-brand)] text-[var(--color-bg-base)]',
            'hover:bg-[var(--color-brand-hover)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors'
          )}
          data-testid="whatif-run-button"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run What-If
            </>
          )}
        </button>

        <button
          onClick={handleReset}
          disabled={isLoading}
          className={cn(
            'flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm',
            'bg-[var(--color-bg-elevated)] border border-[var(--color-border)]',
            'text-[var(--color-text-secondary)]',
            'hover:bg-[var(--color-bg-hover)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors'
          )}
          aria-label="Reset form"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Results */}
      {hasResult && (
        <div className="mt-6 space-y-4 border-t border-[var(--color-border)] pt-4" data-testid="whatif-result">
          {/* Result Header */}
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-[var(--color-text-primary)]">
              Result
            </h4>
            <StatusBadge
              text={result.persisted ? 'Persisted' : 'Exploratory'}
              variant={result.persisted ? 'success' : 'neutral'}
              size="sm"
            />
          </div>

          {/* Duration */}
          {result.duration_ms > 0 && (
            <div className="text-xs text-[var(--color-text-muted)]">
              Completed in {result.duration_ms}ms
            </div>
          )}

          {/* Note */}
          {result.note && (
            <div className="text-xs text-[var(--color-text-muted)] italic">
              {result.note}
            </div>
          )}

          {/* Diff vs Current */}
          {diffItems.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-label)]">
                Changes vs Current
              </h5>

              <div className="space-y-2">
                {diffItems.slice(0, 10).map((item) => {
                  const delta = extractDelta(result.diff_vs_current, item.key);
                  const moneyValue = extractMoneyValue(result.diff_vs_current, item.key);

                  return (
                    <div
                      key={item.key}
                      className="p-3 rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border)]"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[var(--color-text-muted)]">
                          {item.label}
                        </span>
                        {delta && (
                          <span
                            className={cn(
                              'text-xs font-mono',
                              delta.delta > 0
                                ? 'text-[var(--color-verified)]'
                                : delta.delta < 0
                                  ? 'text-[var(--color-text-muted)]'
                                  : 'text-[var(--color-text-dim)]'
                            )}
                          >
                            {delta.delta > 0 ? '+' : ''}
                            {delta.pctChange.toFixed(1)}%
                          </span>
                        )}
                      </div>

                      <div className="mt-1">
                        {moneyValue !== null ? (
                          <FinancialValue value={moneyValue} size="lg" />
                        ) : (
                          <span className="text-sm text-[var(--color-text-primary)]">
                            {String(item.value)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {diffItems.length > 10 && (
                <div className="text-xs text-[var(--color-text-muted)] text-center">
                  +{diffItems.length - 10} more changes
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {result.error && (
            <div
              className="p-3 rounded-md text-sm"
              style={{
                backgroundColor: 'rgba(255, 102, 0, 0.12)',
                border: '1px solid rgba(255, 102, 0, 0.30)',
                color: 'var(--color-brand)',
              }}
            >
              <div className="font-medium">Engine Error</div>
              <div className="opacity-90">{result.error}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WhatIfForm;
